import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { PULSE_FEATURE_NAME } from '@epicuro-next/constants/rule-engine/pulse-rules';
import { FilterQueryParams } from '@epicuro-next/models/products';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import {
  ConfigurationValue,
  initConfigSuccess,
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys,
  PBasketConfigurations,
  pBasketConfigurationsDefaultValues,
  selectConfigurationValueFrom,
  selectIsCatalogOnlyModeRule,
  selectIsSearchV2Enabled,
} from '@epicuro-next/platform/configuration';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import { loadDab } from '@epicuro-next/state/dab-state';
import {
  enrichProductsWithPrices,
  selectMerchandisePricesState,
  setLoadedProductDiscounts,
  State as DiscountsState,
} from '@epicuro-next/state/deprecated-merchandise-prices';
import {
  orderedProductsBrandIdUrlParam,
  selectCatalogOnlyDefaultRule,
  selectCatalogOnlyModeEnabledSettingValue,
  selectResetDefaultPresetOnAccountChangeEnabled,
} from '@epicuro-next/state/p-basket-state';
import {
  selectQueryParams,
  updateQueryParameters,
} from '@epicuro-next/state/router-store';
import { selectSiteId } from '@epicuro-next/state/session-state';
import {
  setSelectedSite,
  updateCurrentSite,
} from '@epicuro-next/state/site-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import {
  canBeBoolean,
  decodeUriComponentRecursively,
  isEqual,
  isNonNullish,
  toBoolean,
  transformFilterQueryParamToSearchQuery,
} from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { RouterNavigatedAction, ROUTER_NAVIGATED } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import {
  Filter,
  Product,
  ProductKey,
  SearchQuery,
  SearchSortType,
  SortDirection,
  Sorting,
  SortingDirection,
} from 'graphql-api/gql-api-interfaces';
import { combineLatest, merge, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { initialFilterQueryParams } from '../../constants';
import { ExtendedFilterQueryParams } from '../../model';
import { ProductBasketService } from '../../services';
import * as productBasketActions from '../product-basket.actions';
import {
  selectCollapse,
  selectDefaultSortingConfig,
  selectPresetValue,
  selectProductBasketQueryParamsExtended,
  selectProductBasketSearchQuery,
  selectProducts,
} from '../product-basket.selectors';

/**
 * Product Basket Effects
 */
@Injectable()
export class ProductBasketEffects {
  private activeAccountId$ = this.store.pipe(
    select(selectActiveAccountId),
    filter(isNonNullish),
    distinctUntilChanged(),
  );

  private siteId$ = this.store.pipe(
    select(selectSiteId),
    filter(isNonNullish),
    distinctUntilChanged(),
  );

  private searchParameters$ = this.store.pipe(
    select(selectProductBasketQueryParamsExtended),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private currentPresetValue$ = this.store.pipe(select(selectPresetValue));

  private ruleEnginePresetValue$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        PBasketConfigurationKeys.DefaultPreset,
        PBasketConfigurations
      >(NAMESPACE_PRODUCT_BASKET, PBasketConfigurationKeys.DefaultPreset, ''),
    ),
    filter(Boolean),
  );

  private ruleEngineCollapseValue$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        PBasketConfigurationKeys.DefaultGroupStyleVariants,
        PBasketConfigurations
      >(
        NAMESPACE_PRODUCT_BASKET,
        PBasketConfigurationKeys.DefaultGroupStyleVariants,
        false,
      ),
    ),
    filter(isNonNullish),
  );

  private ruleEngineSortingValue$ = this.store.pipe(
    select(selectDefaultSortingConfig),
  );

  private readonly useV2Search$ = this.store.pipe(
    select(selectIsSearchV2Enabled),
  );
  private readonly useCatalogOnlyMode$ = combineLatest([
    this.store.pipe(select(selectIsCatalogOnlyModeRule)),
    this.store.pipe(select(selectCatalogOnlyModeEnabledSettingValue)),
  ]).pipe(
    map(([ruleEnabled, settingEnabled]) => !!ruleEnabled && settingEnabled),
  );

  /**
   * Effect to load the products in the p-basket
   */
  public loadProductBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productBasketActions.loadProducts),
      debounceTime(1000),
      // wait required data for gql request
      switchMap(() => this.siteId$.pipe(take(1))),
      concatLatestFrom(() => [
        this.searchParameters$,
        this.currentPresetValue$,
        this.useV2Search$,
        this.useCatalogOnlyMode$,
      ]),
      switchMap(([, params, presetValue, useV2Search, useCatalogOnlyMode]) =>
        this.productBasketService
          .getProducts(
            transformFilterQueryParamToSearchQuery({
              ...params,
              useV2Search,
              useCatalogOnlyMode,
              preset: presetValue,
            }) as SearchQuery,
            false,
          )
          .pipe(
            withLatestFrom(this.store.select(selectMerchandisePricesState)),
            map(
              ([{ products, resultCount }, discounts]: [
                ProductSearchResults,
                DiscountsState,
              ]) =>
                productBasketActions.loadProductsSuccess({
                  products: enrichProductsWithPrices(products, discounts),
                  resultCount,
                }),
            ),
            // Need to cancel switch map stream because of debounce effect
            takeUntil(
              this.actions$.pipe(
                ofType(productBasketActions.loadProducts),
                filter(({ clear }) => clear),
              ),
            ),
            catchError((res: { message: string }) =>
              of(
                productBasketActions.loadProductsFailure({
                  error: res.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public updateProductsWithDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setLoadedProductDiscounts),
      withLatestFrom(
        this.store.select(selectMerchandisePricesState),
        this.store.select(selectProducts),
      ),
      filter(([, , products]) => products.length > 0),
      map(([, merchandisePrices, products]) =>
        productBasketActions.updateProductsWithDiscounts({
          products: enrichProductsWithPrices(products, merchandisePrices),
        }),
      ),
    ),
  );

  public exportProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productBasketActions.exportProductsSearchResult),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(this.searchParameters$, this.currentPresetValue$),
        ),
      ),
      switchMap(([, params, presetValue]) => {
        const filterQueryParam = {
          ...params,
          preset: presetValue,
        };
        const searchQuery = transformFilterQueryParamToSearchQuery(
          filterQueryParam,
        ) as SearchQuery;

        return this.productBasketService.exportProducts(searchQuery).pipe(
          switchMap(() => [
            productBasketActions.exportProductsSearchResultSuccess(),
            showSnackBarMessage({
              message: $localize`:pbasket|Export products success@@exportSearchProductsSuccess:We are now preparing your content pack, you will receive an email as soon as it is ready!`,
              messageType: MessageType.SUCCESS,
            }),
          ]),
          catchError((res: { message: string }) => [
            productBasketActions.exportProductsSearchResultFailed({
              error: res.message,
            }),
            showSnackBarMessage({
              message: $localize`:pbasket|Export products failed@@exportSearchProductsFailed:Your content pack will not be prepared because something went wrong`,
              messageType: MessageType.WARNING,
            }),
          ]),
        );
      }),
    ),
  );

  public initializeProductBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      filter((action: RouterNavigatedAction) =>
        action.payload.routerState.url.includes(
          `/${APP_PRIVATE_PATHS.search.path}`,
        ),
      ),
      take(1),
      switchMap((action) =>
        this.siteId$.pipe(
          map(() => action),
          take(1),
        ),
      ),
      switchMap((action) =>
        this.activeAccountId$.pipe(
          map(() => action),
          take(1),
        ),
      ),
      take(1),
      map(() => productBasketActions.loadProducts({ clear: true })),
    ),
  );

  private readonly externalChanges$ = combineLatest([
    this.activeAccountId$,
    this.siteId$,
  ]).pipe(
    switchMap(([accountId, siteId]) => {
      // wait for the configuration to be loaded, can be deleted after search v2 is enabled for everybody
      return this.actions$.pipe(
        ofType(initConfigSuccess),
        filter(({ configurations }) =>
          isNonNullish(configurations[PULSE_FEATURE_NAME]),
        ),
        take(1),
        concatLatestFrom(() => this.useV2Search$),
        map(([_, useV2Search]) => ({
          accountId,
          siteId,
          useV2Search,
        })),
      );
    }),
    distinctUntilChanged(isEqual),
  );

  public catalogOnlyModeChange$ = this.actions$.pipe(
    ofType(productBasketActions.setCatalogOnlyMode),
    map(({ value }) => value),
    distinctUntilChanged(),
  );

  public externalChange$ = createEffect(() =>
    merge(this.externalChanges$, this.catalogOnlyModeChange$).pipe(
      switchMap(() => this.apollo.getClient().cache.reset()),
      map(() => productBasketActions.loadProducts({ clear: true })),
    ),
  );

  public filtersChange$ = createEffect(() =>
    this.searchParameters$.pipe(
      map(stripPageParameter),
      distinctUntilChanged(isEqual),
      skip(1),
      map(() => productBasketActions.loadProducts({ clear: true })),
    ),
  );

  /**
   * Effect to update the url when filters/sorting changes
   */
  public updateUrlOnToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productBasketActions.toggleFilter),
      withLatestFrom(this.searchParameters$, this.currentPresetValue$),
      map(
        ([
          { replaceUrl },
          { start: __, num: ___, site: ____, q, f, ...params },
          preset,
        ]) => {
          return updateQueryParameters({
            queryParams: getFiltersNavigationParams(params, f, q, preset),
            replaceUrl,
          });
        },
      ),
    ),
  );

  public updateUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          productBasketActions.removeFiltersById,
          productBasketActions.setSortingParams,
          productBasketActions.setSearchQuery,
          productBasketActions.setCollapseParam,
        ),
        concatLatestFrom(() => [
          this.currentPresetValue$,
          this.searchParameters$,
        ]),
        tap(
          ([
            _,
            preset,
            { start: __, num: ___, site: ____, q, f, ...params },
          ]) => {
            this.updateSearchQueryParams(params, f, q, preset);
          },
        ),
      ),
    { dispatch: false },
  );

  public updateUrlOnClearFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(productBasketActions.clearFilters),
        filter(({ skipParams }) => !skipParams),
        concatLatestFrom(() => {
          return [this.searchParameters$, this.currentPresetValue$];
        }),
        tap(
          ([
            _,
            { start: __, num: ___, site: ____, q, f, ...params },
            preset,
          ]) => {
            this.updateSearchQueryParams(params, f, q, preset);
          },
        ),
      ),
    { dispatch: false },
  );

  public updateUrlOnRemoveFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(productBasketActions.removeFilters),
        concatLatestFrom(() => {
          return [this.searchParameters$, this.currentPresetValue$];
        }),
        tap(
          ([
            _,
            { start: __, num: ___, site: ____, q, f, ...params },
            preset,
          ]) => {
            this.updateSearchQueryParams(params, f, q, preset);
          },
        ),
      ),
    { dispatch: false },
  );

  /**
   * Effect to get the request parameters from the url.
   */
  public getParamsFromUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      distinctUntilChanged(compareRouterStates),
      filter((action: RouterNavigatedAction) =>
        action.payload.routerState.url.includes(
          `/${APP_PRIVATE_PATHS.search.path}`,
        ),
      ),
      withLatestFrom(
        this.store.pipe(select(selectQueryParams)),
        this.searchParameters$,
      ),
      switchMap(([, queryParams, stateParams]) => {
        if (queryParams.q) {
          queryParams = {
            ...queryParams,
            q: decodeUriComponentRecursively(queryParams.q),
          };
        }

        const actions = [];
        actions.push(
          productBasketActions.setParamsFromURL({
            filters: mergeParams(queryParams, stateParams),
            preset: queryParams.preset ?? stateParams.preset,
            collapse: canBeBoolean(queryParams.collapse)
              ? toBoolean(queryParams.collapse)
              : undefined,
            orderedProductsBrandId:
              queryParams[orderedProductsBrandIdUrlParam] ?? undefined,
          }),
        );
        if (queryParams.hasOwnProperty('removeFilters')) {
          actions.push(productBasketActions.removeFilters());
        }
        return actions;
      }),
    ),
  );

  public resetQueryOnNavigationFromSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      filter(
        (action: RouterNavigatedAction) =>
          !action.payload.routerState.url.includes(
            `/${APP_PRIVATE_PATHS.search.path}`,
          ),
      ),
      concatLatestFrom(() => [
        this.store.pipe(select(selectProductBasketSearchQuery)),
      ]),
      filter(([, query]) => !!query),
      map(() => productBasketActions.setSearchQuerySilent({ query: '' })),
    ),
  );

  private searchFiltersParameters$: Observable<
    [ExtendedFilterQueryParams, ExtendedFilterQueryParams]
  > = this.searchParameters$.pipe(
    filter(isNonNullish),
    distinctUntilChanged(isEqual),
    startWith(null as unknown as ExtendedFilterQueryParams),
    // to remember previous search params and so use it on UI & GQL layers for optimization purposes
    pairwise(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private showEmptyFilters$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        PBasketConfigurationKeys.ShowEmptyFilterOptions,
        PBasketConfigurations
      >(
        NAMESPACE_PRODUCT_BASKET,
        PBasketConfigurationKeys.ShowEmptyFilterOptions,
        pBasketConfigurationsDefaultValues[
          PBasketConfigurationKeys.ShowEmptyFilterOptions
        ],
      ),
    ),
    map(Boolean),
  );

  private loadFiltersTriggerSource$ = combineLatest([
    this.searchFiltersParameters$.pipe(
      distinctUntilChanged(
        ([, prevCurrFiltersParams], [, currCurrFiltersParams]) =>
          isEqual(
            stripFiltersIrrelevantParams(
              prevCurrFiltersParams as unknown as FiltersIrrelevantParams,
            ),
            stripFiltersIrrelevantParams(
              currCurrFiltersParams as unknown as FiltersIrrelevantParams,
            ),
          ),
      ),
    ),
    this.actions$.pipe(ofType(productBasketActions.loadFilters)),
    this.externalChanges$, // to trigger filters load when we change search version
  ]).pipe(
    // So search request happens only if URL params have changed
    distinctUntilChanged(
      (
        [searchParam1, , externalChanges1],
        [searchParam2, , externalChanges2],
      ) => {
        return (
          isEqual(searchParam1, searchParam2) &&
          // search params may not change, but we can change account with default search params
          isEqual(externalChanges1, externalChanges2)
        );
      },
    ),
    map(([searchFiltersParameters]) => searchFiltersParameters),
  );

  public loadDefaultFilters$ = createEffect(() =>
    this.loadFiltersTriggerSource$.pipe(
      withLatestFrom(this.showEmptyFilters$, this.useV2Search$),
      filter(([, enabled]) => !!enabled),
      debounceTime(200),
      switchMap(([[prev, filters], _, useV2Search]) => {
        const protectedFilters = ['preset', 'subsites', 'collapse'];
        const defaultFilters = protectedFilters.reduce(
          (acc, fieldId: keyof FilterQueryParams) => ({
            ...acc,
            [fieldId]: filters[fieldId],
          }),
          initialFilterQueryParams,
        );

        return this.productBasketService
          .getFilters({
            previousSearchQuery: prev as SearchQuery,
            currentSearchQuery: defaultFilters as SearchQuery,
            useV2Search,
          })
          .pipe(
            map((res) =>
              productBasketActions.loadDefaultFiltersSuccess({
                sorting: (res?.sorting || []) as Sorting[],
                filters: (res?.filters || []) as Filter[],
              }),
            ),
          );
      }),
    ),
  );

  /**
   * Effect to load the filters in the p-basket
   */

  public loadFilters$ = createEffect(() =>
    this.loadFiltersTriggerSource$.pipe(
      debounceTime(200),
      switchMap(() =>
        combineLatest([
          this.searchFiltersParameters$,
          this.siteId$.pipe(filter(isNonNullish)),
          this.activeAccountId$.pipe(filter(isNonNullish)),
        ]).pipe(
          withLatestFrom(this.useV2Search$),
          take(1),
          switchMap(([[[prevParams, currentParams]], useV2Search]) => {
            const previousSearchQuery =
              transformFilterQueryParamToSearchQuery(prevParams);
            const currentSearchQuery: SearchQuery =
              transformFilterQueryParamToSearchQuery(
                currentParams,
              ) as SearchQuery;

            return this.productBasketService
              .getFilters({
                previousSearchQuery,
                currentSearchQuery,
                useV2Search,
              })
              .pipe(
                map((res) =>
                  productBasketActions.loadFiltersSuccess({
                    sorting: (res?.sorting || []) as Sorting[],
                    filters: (res?.filters || []) as Filter[],
                    previousSearchQuery,
                    currentSearchQuery,
                  }),
                ),
                catchError((res: { message: string }) =>
                  of(
                    productBasketActions.loadFiltersFailure({
                      error: res.message,
                    }),
                  ),
                ),
              );
          }),
        ),
      ),
    ),
  );

  /*
   * Effects to trigger a dab load after a search
   */
  public loadProductSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productBasketActions.loadProductsSuccess),
      withLatestFrom(this.store.pipe(select(selectCollapse))),
      map(([{ products }, collapse]) => {
        const payload: ProductKey[] = products.map((item) => ({
          productId: collapse
            ? item.parentId ?? item.productId
            : item.productId,
          brandId: item.brandId,
        }));

        return loadDab({
          products: payload,
        });
      }),
    ),
  );

  /*
   * Effects to trigger media relations load after a search
   */
  public mediaRelationsLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productBasketActions.loadProductsSuccess),
      withLatestFrom(this.store.pipe(select(selectCollapse))),
      // tslint:disable-next-line:no-identical-functions
      map(([{ products }, collapse]) => {
        const payload: ProductKey[] = products.map((item) => ({
          productId: collapse
            ? item.parentId ?? item.productId
            : item.productId,
          brandId: item.brandId,
        }));

        return loadDab({
          products: payload,
        });
      }),
    ),
  );

  /**
   * Check the default preset filter value on rule engine
   * on initialization
   */
  public initConfigSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initConfigSuccess),
      filter(({ configurations }) =>
        isNonNullish(configurations[NAMESPACE_PRODUCT_BASKET]),
      ),
      concatLatestFrom(() => [
        this.ruleEnginePresetValue$,
        this.store.pipe(
          select(selectResetDefaultPresetOnAccountChangeEnabled),
          map(Boolean),
        ),
      ]),
      tap(),
      map(([, ruleEnginePresetValue, resetToDefaultPreset]) =>
        productBasketActions.setRuleEnginePresetValue({
          defaultPreset: ruleEnginePresetValue,
          resetToDefaultPreset,
        }),
      ),
    ),
  );

  public initCatalogOnlyMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initConfigSuccess),
      filter(({ configurations }) =>
        isNonNullish(configurations[NAMESPACE_PRODUCT_BASKET]),
      ),
      concatLatestFrom(() => this.store.select(selectCatalogOnlyDefaultRule)),
      take(1),
      map(([, catalogOnlyMode]) =>
        productBasketActions.setCatalogOnlyMode({
          value: !!catalogOnlyMode,
        }),
      ),
    ),
  );

  public initCollapseValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initConfigSuccess),
      filter(({ configurations }) =>
        isNonNullish(configurations[NAMESPACE_PRODUCT_BASKET]),
      ),
      concatLatestFrom(() => this.ruleEngineCollapseValue$),
      take(1),
      map(([, ruleEngineCollapseValue]) =>
        productBasketActions.setRuleEngineCollapseValue({
          defaultCollapse: ruleEngineCollapseValue,
        }),
      ),
    ),
  );

  public initSortingValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initConfigSuccess),
      filter((action) => {
        const namespace: ConfigurationValue | undefined =
          action.configurations[NAMESPACE_PRODUCT_BASKET];

        return Object.keys(namespace ?? {}).some(
          (key) => key === PBasketConfigurationKeys.DefaultSortingConfig,
        );
      }),
      concatLatestFrom(() => [this.ruleEngineSortingValue$]),
      take(1),
      map(([, sorting]) => {
        return productBasketActions.initSortingParams({
          sortField: sorting.field,
          sortDirection: sorting.direction,
        });
      }),
    ),
  );

  public setSubsites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSelectedSite),
      filter(({ site }) => {
        return site?.subSiteId !== undefined;
      }),
      concatMap(({ site, skipLocationChange }) => {
        const actions = !skipLocationChange
          ? [productBasketActions.resetFilters()]
          : [];

        return [
          ...actions,
          productBasketActions.setSubsites({
            subsites: site?.subSiteId ?? '',
          }),
        ];
      }),
    ),
  );

  public updateCurrentSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCurrentSite),
      distinctUntilChanged(
        (previous, current) =>
          previous.site.subSiteId === current.site.subSiteId,
      ),
      filter(({ site }) => {
        return site?.subSiteId !== undefined;
      }),
      concatMap(({ site }) => {
        return [
          productBasketActions.resetFilters(),
          productBasketActions.setSubsites({
            subsites: site?.subSiteId ?? '',
          }),
        ];
      }),
    ),
  );

  public resetSortingIfOrderHistorySorting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        productBasketActions.toggleFilter,
        productBasketActions.removeFiltersById,
        productBasketActions.clearFilters,
        productBasketActions.resetFilters,
      ),
      concatLatestFrom(() =>
        this.store.pipe(select(selectProductBasketQueryParamsExtended)),
      ),
      filter(
        ([, params]) => params.sortField === SearchSortType.BoughtThisSeason,
      ),
      map(() =>
        productBasketActions.setSortingParams({
          sortField: 'Default',
          sortDirection: SortingDirection.Desc,
        }),
      ),
    ),
  );

  private updateSearchQueryParams(
    params: Params,
    f: string | null,
    q: string | null,
    preset: string | undefined,
  ) {
    this.router
      .navigate([{ outlets: { overlay: null } }], {
        skipLocationChange: true,
        queryParamsHandling: 'preserve',
      })
      .then(() => {
        this.router.navigate([], {
          queryParams: getFiltersNavigationParams(params, f, q, preset),
        });
      });
  }

  constructor(
    private actions$: Actions,
    private productBasketService: ProductBasketService,
    private store: Store,
    private apollo: Apollo,
    private router: Router,
  ) {}
}

function compareRouterStates(
  previous: RouterNavigatedAction,
  next: RouterNavigatedAction,
) {
  return previous.payload.routerState.url === next.payload.routerState.url;
}

function mergeParams(
  queryParams: Params,
  stateParams: FilterQueryParams,
): FilterQueryParams {
  return {
    q: queryParams.q ?? '',
    f: queryParams.f ?? stateParams.f ?? '',
    subsites: queryParams.subsites ?? stateParams.subsites ?? '',
    availability: queryParams.availability ?? stateParams.availability ?? '',
    maxAge: queryParams.maxAge ?? stateParams.maxAge ?? '',
    rrp: queryParams.rrp ?? stateParams.rrp ?? '',
    wsp: queryParams.wsp ?? stateParams.wsp ?? '',
    sortField: queryParams.sortField ?? stateParams.sortField,
    sortDirection: queryParams.sortDirection ?? stateParams.sortDirection,
    inventoryRange:
      queryParams.inventoryRange ?? stateParams.inventoryRange ?? '',
    isdDates: queryParams.isdDates ?? stateParams.isdDates ?? '',
    filterOutInbound:
      queryParams.filterOutInbound ?? stateParams.filterOutInbound,
    filterOutRecentlyDelivered:
      queryParams.filterOutRecentlyDelivered ??
      stateParams.filterOutRecentlyDelivered,
    orderHistoryFromDate:
      queryParams.orderHistoryFromDate ?? stateParams.orderHistoryFromDate,
    orderHistoryToDate:
      queryParams.orderHistoryToDate ?? stateParams.orderHistoryToDate,
  };
}

function getFiltersNavigationParams(
  params: Params,
  f: string | null,
  q: string | null,
  preset: string | undefined,
) {
  const query = q !== '' ? { q } : { q: null };
  const facets = f !== '' ? { f } : { f: null };

  return {
    ...params,
    ...query,
    ...facets,
    subsites: params.subsites ?? null,
    preset,
  };
}

function stripPageParameter<T extends Paginated>(
  object: T,
): Omit<T, keyof Paginated> {
  const returnObject = { ...object };

  delete returnObject.num;
  delete returnObject.start;

  return returnObject;
}

function stripFiltersIrrelevantParams<T extends FiltersIrrelevantParams>(
  object: T,
  // tslint:disable-next-line:max-union-size
): Omit<T, 'start' | 'num' | 'sortDirection' | 'sortField'> {
  const returnObject = { ...object };

  delete returnObject.num;
  delete returnObject.start;
  delete returnObject.sortDirection;
  delete returnObject.sortField;

  return returnObject;
}

interface Paginated {
  start?: number;
  num?: number;
}

interface FiltersIrrelevantParams {
  start?: number;
  num?: number;
  sortDirection?: SortDirection;
  sortField?: SearchSortType;
}

interface ProductSearchResults {
  products: Product[];
  resultCount: number;
}
